import React, {useEffect} from 'react';
import {Box, Typography} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import {
  AppleLogoWhite,
  PlayStore,
  circularTick,
  Thunder,
  BadgeTick,
  Cal,
  Send,
} from '@jobseeker/lotus/assets/icons';
import {FreeProJFQR} from '@jobseeker/lotus/assets/images';
import {getUbaPageName, TrackEventUBA} from '@jobseeker/common-core/utils';
import { FEATURE_LIST } from '@jobseeker/common-core/constants';

type FreeProBannerProps = {
  variant?: 'default' | 'applied';
  QR?: any;
  onPlayStoreClick?: () => void;
  onAppStoreClick?: () => void;
};



const FreeBadge = () => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      background: '#49966E',
      borderRadius: '4px',
      px: 0.625,
      py: 0,
      mx: 0.5,
      mt: -0.5,
      verticalAlign: 'middle',
    }}
  >
    <Typography
      component="span"
      sx={{
        fontSize: 17,
        fontWeight: 600,
        color: '#fff',
        lineHeight: '1.2',
      }}
    >
      FREE
    </Typography>
  </Box>
);

type StoreButtonProps = {
  logoSrc: string;
  logoAlt: string;
  first: string;
  second: string;
  imgWidth: number;
  imgHeight: number;
  handleClick: () => void;
};

const StoreButton: React.FC<StoreButtonProps> = ({
  logoSrc,
  logoAlt,
  first,
  second,
  imgWidth,
  imgHeight,
  handleClick,
}) => (
  <Box
    onClick={handleClick}
    display="flex"
    justifyContent="center"
    alignItems="center"
    borderRadius={2}
    p={0.75}
    sx={{backgroundColor: 'text.primary', cursor: 'pointer', minWidth: 86}}
  >
    <Box sx={{marginRight: '4px'}} display="flex">
      <Image src={logoSrc} alt={logoAlt} width={imgWidth} height={imgHeight} />
    </Box>
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="flex-start"
    >
      <Typography variant="body2" fontSize={6} color="#fff" lineHeight="8px">
        {first}
      </Typography>
      <Typography
        variant="subtitle2"
        fontSize={9}
        color="#fff"
        lineHeight="8px"
      >
        {second}
      </Typography>
    </Box>
  </Box>
);

const FreeProBanner: React.FC<FreeProBannerProps> = ({
  variant = 'default',
  QR = FreeProJFQR,
  onPlayStoreClick,
  onAppStoreClick,
}) => {
  const isApplied = variant === 'applied';
  const linkageName = `Free Pro Membership App Linkage`;
  const linkageType = 'QR';
  const PLATFORM = process.env.NEXT_PUBLIC_APP_NAME as 'iimjobs' | 'hirist';

  useEffect(() => {
    TrackEventUBA('appDownloadLinkageView', 'view', {
      platform: PLATFORM,
      linkageName,
      linkageType,
      pageName: getUbaPageName(),
      sessionUsername: sessionStorage.getItem('userId') || '',
    });
  }, []);

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: '10px',
        bgcolor: isApplied ? 'rgba(245, 250, 247, 0.9)' : 'white',
        px: 3,

        pt: isApplied ? 1.5 : 2,
        pb: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
        border: '1px solid #CCE7DB',
        background: 'linear-gradient(90deg, #E4F7EE 0%, #FFF 100%)',
      }}
    >
      {/* Left Section */}
      <Box sx={{flex: 1}}>
        {/* Top Tag */}
        {isApplied ? (
          <Box display="inline-flex" alignItems="center" gap={1}>
            <Image
              alt="some image"
              src={Send}
              style={{width: 24, height: 24}}
            />
            <Typography fontSize={12} fontWeight={600} color="#159076">
              Application Sent!
            </Typography>
          </Box>
        ) : (
          <Box
            display="inline-flex"
            alignItems="center"
            sx={{
              backgroundColor: '#333',
              borderRadius: '6px',
              px: 1,
              py: 0.75,
              mb: 1,
            }}
          >
            <Typography
              fontSize={10}
              fontWeight={500}
              color="white"
              letterSpacing={0.5}
            >
              APP EXCLUSIVE OFFER
            </Typography>
          </Box>
        )}

        {/* Title */}
        <Typography
          fontWeight={600}
          fontSize={17}
          color="text.primary"
          mb={1.25}
          component="div"
        >
          {isApplied ? (
            <>
              Compare yourself with the competition – with
              <FreeBadge /> Pro access on the app
            </>
          ) : (
            <>
              Get 30 days of Pro access
              <FreeBadge /> - install the app to activate
            </>
          )}
        </Typography>

        {isApplied ? (
          <>
            {/* Features Row */}
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              flexWrap="wrap"
              mb={1.5}
            >
              {FEATURE_LIST.map(feature => (
                <Box key={feature} display="flex" alignItems="center" gap={0.5}>
                  <Image
                    src={circularTick}
                    alt="tick"
                    style={{height: 14, width: 14}}
                  />
                  <Typography fontSize={12} fontWeight={400} color="#49966E">
                    {feature}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Footer Tags */}
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <Box display="flex" alignItems="center" gap={0.5}>
                <Image
                  src={Cal}
                  alt="tick"
                  style={{height: 14, width: 14, marginTop: -0.25}}
                />
                <Typography
                  fontSize={10}
                  fontWeight={400}
                  color="rgba(51, 51, 51, 0.60)"
                >
                  30 days access
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" ml={-0.5}>
                <Image
                  src={Thunder}
                  alt="tick"
                  style={{height: 20, width: 20}}
                />
                <Typography
                  fontSize={10}
                  fontWeight={400}
                  color="rgba(51, 51, 51, 0.60)"
                >
                  Activates instantly on login
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Image
                  src={BadgeTick}
                  alt="tick"
                  style={{height: 14, width: 14}}
                />{' '}
                <Typography
                  fontSize={10}
                  fontWeight={400}
                  color="rgba(51, 51, 51, 0.60)"
                >
                  No card required
                </Typography>
              </Box>
            </Box>
          </>
        ) : (
          /* Default variant: grid so each feature aligns above its footer tag */
          <Box
            display="grid"
            gridTemplateColumns="repeat(3, auto)"
            columnGap={2}
            rowGap={1.5}
            sx={{width: 'fit-content'}}
          >
            {/* Row 1: Features */}
            {FEATURE_LIST.map(feature => (
              <Box key={feature} display="flex" alignItems="center" gap={0.5}>
                <Image
                  src={circularTick}
                  alt="tick"
                  style={{height: 14, width: 14}}
                />
                <Typography fontSize={12} fontWeight={400} color="#49966E">
                  {feature}
                </Typography>
              </Box>
            ))}

            {/* Row 2: Footer Tags */}
            <Box display="flex" alignItems="center" ml={-0.5}>
              <Image src={Thunder} alt="tick" style={{height: 20, width: 20}} />
              <Typography
                fontSize={10}
                fontWeight={400}
                color="rgba(51, 51, 51, 0.60)"
              >
                Activates instantly on login
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Image
                src={BadgeTick}
                alt="tick"
                style={{height: 14, width: 14}}
              />{' '}
              <Typography
                fontSize={10}
                fontWeight={400}
                color="rgba(51, 51, 51, 0.60)"
              >
                No card required
              </Typography>
            </Box>
          </Box>
        )}

        {/* Store Buttons – applied variant only */}
        {isApplied && (
          <Box display="flex" gap={1} mt={1.5}>
            <StoreButton
              handleClick={
                onPlayStoreClick
                  ? () => {
                      TrackEventUBA('appDownloadLinkageClick', 'click', {
                        platform: PLATFORM,
                        linkageName,
                        linkageType,
                        pageName: getUbaPageName(),
                        typeClick: 'Playstore',
                        sessionUsername: sessionStorage.getItem('userId') || '',
                      });
                      onPlayStoreClick();
                    }
                  : () => {}
              }
              logoSrc={PlayStore}
              logoAlt="playStore"
              first="Get it on"
              second="Google Play"
              imgWidth={14}
              imgHeight={14}
            />
            <StoreButton
              handleClick={
                onAppStoreClick
                  ? () => {
                      TrackEventUBA('appDownloadLinkageClick', 'click', {
                        platform: PLATFORM,
                        linkageName,
                        linkageType,
                        pageName: getUbaPageName(),
                        typeClick: 'AppStore',
                        sessionUsername: sessionStorage.getItem('userId') || '',
                      });
                      onAppStoreClick();
                    }
                  : () => {}
              }
              logoSrc={AppleLogoWhite}
              logoAlt="apple"
              first="Download on"
              second="App Store"
              imgWidth={16}
              imgHeight={16}
            />
          </Box>
        )}
      </Box>

      {/* Right Section – QR Code */}
      <Box display="flex" flexDirection="column" alignItems="center">
        <Typography
          fontSize={12}
          fontWeight={500}
          mb={0.75}
          sx={{textAlign: 'center'}}
        >
          Scan to Install App
        </Typography>
        <Box
          sx={{
            borderRadius: 4,
            p: 1,
            background: '#CCE7DB',
          }}
        >
          <Image
            src={QR}
            width={isApplied ? 110 : 86}
            height={isApplied ? 110 : 86}
            style={{marginBottom: '-4px'}}
            alt="freeProQr"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default FreeProBanner;
